// pages/orders/OrderDetail.jsx
import { useEffect, useState } from "react";
import { ordersAPI }   from "../../services/api";
import { useRouter }   from "../../contexts/RouterContext";
import { useToast }    from "../../contexts/ToastContext";
import { Breadcrumb, Loading, Spinner } from "../../components/ui";
import { fmt, StatusBadge } from "../../utils/helpers";
import ReceiptModal       from "./ReceiptModal";
import AddStitchModal from "./AddStitchModal";
import AddMaterialModal from "./AddMaterialModal";



export default function OrderDetail() {
  const { navigate, route , back} = useRouter();
  const orderId = route?.params?.id;
  const toast         = useToast();
  const [mode, setMode] = useState(route?.params?.mode || "view");
  const isEditMode = mode === "edit";
  const [showAddStitch, setShowAddStitch] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [order,        setOrder]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showReceipt,  setShowReceipt]  = useState(false);
  const [delivering,   setDelivering]   = useState(false);
  const [selectedStitches, setSelectedStitches] = useState([]);
  const [deleteMaterialId, setDeleteMaterialId] = useState(null);
  const [deleteStitchId, setDeleteStitchID] = useState(null);

  const toggleStitch = (id) => {
    setSelectedStitches(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };
 
  const load = async () => {
    setLoading(true);
    const o = await ordersAPI.get(orderId);
    setOrder(o);
    setLoading(false);
  };

  useEffect(() => {
  if (route?.params?.mode) {
    setMode(route.params.mode);
  }
}, [route?.params?.mode]);

  const handleDeliver = async () => {
  if (selectedStitches.length === 0) {
    toast("Select at least one item to deliver", "error");
    return;
  }

  setDelivering(true);
  try {
    // One batched call — the endpoint transacts the whole array, so a partial
    // failure rolls back instead of half-delivering the order.
    await ordersAPI.deliverItems(selectedStitches);

    const n = selectedStitches.length;
    toast(`${n} item${n > 1 ? "s" : ""} delivered to customer ✅`);

    setSelectedStitches([]);
    load();
  } catch (e) {
    console.error(e);
    toast(e.message || "Failed to deliver items", "error");
  } finally {
    setDelivering(false);
  }
};
  const canDeliver = selectedStitches.length > 0 && !delivering;

  useEffect(() => { load(); }, [orderId]);

  // No backend endpoint exists yet to close an order (OM_IsClosed = 1). This used to
  // toast "Order marked as completed! 🎉" with the API call commented out, so the order
  // stayed open while the user believed it was closed. Kept disabled until the endpoint
  // lands — see the Complete button below.
  const handleComplete = () => {
    setMode("view");
    setSelectedStitches([]);
    setShowAddStitch(false);
    setShowAddMaterial(false);

    toast("Order editing completed successfully!");
  };

  const handleDeleteMaterial = async () => {
  if (!deleteMaterialId) return;

  try {
    await ordersAPI.deleteMaterial(deleteMaterialId);
    toast("Material deleted successfully ✅");
    load(); // refresh data
  } catch (e) {
    console.error(e);
    // Surface the backend's reason — it explains *why* a delete was refused.
    toast(e.message || "Failed to delete material", "error");
  } finally {
    setDeleteMaterialId(null);
  }
};
const handleDeleteStitch = async () => {
  if (!deleteStitchId) return;

  try {
    await ordersAPI.deleteStitch(deleteStitchId);
    toast("Stitch deleted successfully ✅");
    load(); // refresh data
  } catch (e) {
    console.error(e);
    toast(e.message || "Failed to delete stitch", "error");
  } finally {
    setDeleteStitchID(null);
  }
};

  if (loading) return <div className="page"><Loading /></div>;

  if (!order) return (
    <div className="page">
      <div className="empty-state">
        <div className="empty-icon">❓</div>
        <div className="empty-title">Order not found</div>
      </div>
    </div>
  );

  return (
  <div className="page" style={{ padding: 20, maxWidth: "100%" }}>

    <Breadcrumb items={[
      { label: "Orders", onClick: () => navigate("orders") },
      { label: order.id },
    ]} />

    {/* HEADER */}
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 20
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 50 }}>
          {/* 🔥 BACK BUTTON */}
          <button
            className="btn btn-ghost"
            onClick={back} // fallback if no history
          >
            ← 
          </button>

          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700 }}>
              Order #{order.id}
            </h1>

            <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
             Order Date: {fmt(order.orderDate)} • Delivery: {fmt(order.deliveryDate)}
            </div>
          </div>

        </div>

      {/* 🔥 ACTION BUTTONS (UNCHANGED LOGIC) */}
      <div style={{ display: "flex", gap: 10 }}>
        <StatusBadge status={order.status} />


      
        {!isEditMode && (
          <button
            className="btn btn-ghost"
            onClick={() => setMode("edit")}
          >
            ✏️ Edit
          </button>
        )}

        {/* Assigning work to a karigar now lives on Karigars → 📤 Assign Work, which
            works across every open order instead of one at a time. */}

        {isEditMode && (
          <button
            className="btn btn-success"
            onClick={handleComplete}
          >
            ✓ Complete
          </button>
        )}

        {isEditMode && (
          <button
            className="btn btn-primary"
            onClick={() => setShowReceipt(true)}
          >
            🧾 Receipt
          </button>
        )}
      </div>
    </div>

    {/* SUMMARY CARDS */}
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 16,
      marginBottom: 20
    }}>
      <div className="card">
        <div className="card-section-title">Customer</div>
        <div
          style={{ fontWeight: 600, cursor: "pointer", color: "var(--gold)" }}
          onClick={() => navigate("customer-detail", { id: order.customerId })}
        >
          {order.customerName}
        </div>
      </div>

      {/* <div className="card">
        <div className="card-section-title">Karigar</div>
        <div>{order.assignedKarigarName || "Not Assigned"}</div>
      </div> */}

      <div className="card">
        <div className="card-section-title">Amount</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--gold)" }}>
          ₹{order.amount}
        </div>
      </div>

      <div className="card">
        <div className="card-section-title">Status</div>
        <StatusBadge status={order.status} />
      </div>
    </div>

    {/* MAIN GRID */}
    <div style={{
      display: "grid",
      gridTemplateColumns: "2fr 1fr",
      gap: 20
    }}>

      {/* LEFT SIDE */}
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* STITCH TABLE */}
        <div className="card">
          <div className="card-section-title">✂️ Stitch Details</div>
            {isEditMode && (
              <div style={{ marginBottom: 10 }}>
                <button className="btn btn-primary" onClick={() => setShowAddStitch(true)}>
                  ➕ Add New Stitch
                </button>
              </div>
            )}

          <table style={{ width: "100%", marginTop: 10 }}>
            <thead>
              <tr>
                <th></th> {/* checkbox column */}
                <th>Type</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Total</th>
               {isEditMode && <th>Actions</th>}
              </tr>
            </thead>
           <tbody>
              {order.stitchDetails?.map((s) => {
                // Stage is derived from the primitive DB flags only. The backend also
                // returns a computed Delivery_Status, but it is 1 for a brand-new stitch,
                // so trusting it made every new item show up as "Delivered".
                const isAssigned  = Number(s.karigarId || 0) > 0;
                const isIssued    = Number(s.karigarIssued || 0) === 1;
                const isReceived  = Number(s.karigarReceived || 0) === 1;
                const isDelivered = !!s.isDelivered;

                // ✅ FINAL RULE: customer delivery unlocks only once the karigar has
                // returned the item, and only while it isn't already delivered.
                const canDeliverSelect = isReceived && !isDelivered;

                const isSelected = selectedStitches.includes(s.id);

                // Remove_Order_Stich_Detail.php refuses to delete a row that is already
                // delivered or already assigned to a karigar (it would corrupt the order
                // total and the karigar's job). Mirror that rule here so the button
                // explains itself instead of round-tripping to a server error.
                const deleteBlockedReason =
                  isDelivered ? "Already delivered to the customer"
                  : isAssigned ? "Assigned to a karigar"
                  : null;

                return (
                  <tr key={s.id}>
                    {/* ✅ CHECKBOX LOGIC */}
                    <td>
                    {canDeliverSelect && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={delivering}
                        onChange={() => toggleStitch(s.id)}
                      />
                    )}
                  </td>
                    {/* TYPE + STATUS */}
                    <td>
                      <div style={{ fontWeight: 600 }}>{s.type}</div>

                      {/* 👷 KARIGAR */}
                      {isAssigned && (
                        <div style={{ fontSize: 12, color: "var(--gold)", marginTop: 4 }}>
                          👷 {s.karigarName}
                        </div>
                      )}

                      {/* Exactly one stage line, most-advanced state first. */}
                      {isDelivered ? (
                        <div style={{ fontSize: 12, color: "green", fontWeight: 600 }}>
                          ✅ Delivered
                        </div>
                      ) : isReceived ? (
                        <div style={{ fontSize: 12, color: "#4caf7a" }}>
                          📥 Received — ready to deliver
                        </div>
                      ) : isIssued ? (
                        <div style={{ fontSize: 12, color: "#e07a3a" }}>
                          📤 Issued to Karigar
                        </div>
                      ) : isAssigned ? (
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          🧷 Assigned — not yet issued
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          🧵 Assign to Karigar
                        </div>
                      )}
                    </td>
                    <td>{s.qty}</td>
                    <td>₹{s.rate}</td>
                    <td>₹{s.qty * s.rate}</td>
                    {isEditMode && (
                      <td>
                        <button
                          className="btn btn-danger"
                          style={{
                            padding: "4px 8px",
                            fontSize: 12,
                            opacity: deleteBlockedReason ? 0.4 : 1,
                            cursor: deleteBlockedReason ? "not-allowed" : "pointer",
                          }}
                          disabled={!!deleteBlockedReason}
                          title={deleteBlockedReason ?? "Delete this stitch item"}
                          onClick={() => setDeleteStitchID(s.id)}
                        >
                          🗑 Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>

          </table>
        </div>

        {/* MATERIAL TABLE */}
        <div className="card">
          <div className="card-section-title">🧶 Material Details</div>
          {isEditMode && (
            <div style={{ marginBottom: 10 }}>
              <button className="btn btn-primary" onClick={() => setShowAddMaterial(true)}>
                ➕ Add New Material
              </button>
            </div>
          )}

          <table style={{ width: "100%", marginTop: 10 }}>
            <thead>
              <tr>
                <th>Material</th>
                <th>Qty</th>
                <th>Rate</th>
                {isEditMode && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
            {order.materialDetails?.map((m, i) => (
              <tr key={i}>
                <td>{m.name}</td>
                <td>{m.quantity}</td>
                <td>₹{m.rate}</td>

                <td>
                  {isEditMode && (
                    <button
                      className="btn btn-danger"
                      style={{ padding: "4px 8px", fontSize: 12 }}
                      onClick={() => setDeleteMaterialId(m.id)}
                    >
                      🗑 Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* NOTES */}
        <div className="card">
          <div className="card-section-title">Notes</div>
          <div style={{ color: "var(--text-muted)" }}>
            {order.remarks || "No remarks"}
          </div>
        </div>

        {/* SUMMARY */}
        <div className="card">
          <div className="card-section-title">Summary</div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Total</span>
            <span>₹{order.amount}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span>Discount</span>
            <span>- ₹{order.discount || 0}</span>
          </div>
          <hr style={{ margin: "10px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
            <span>Final</span>
            <span>₹{order.amount - (order.discount || 0)}</span>
          </div>
        </div>

        {!isEditMode && (
        <div className="card">
          <div className="card-section-title">Delivery</div>
            <button
            className="btn btn-success"
            disabled={!canDeliver}
            style={{ width: "100%" }}
            onClick={handleDeliver}
            >
            {delivering
              ? <><Spinner /> Delivering…</>
              : `🚚 Deliver Selected (${selectedStitches.length})`}
          </button>

          {/* Explain the empty state instead of just showing a dead button. */}
          {!order.stitchDetails?.some((s) => Number(s.karigarReceived || 0) === 1 && !s.isDelivered) && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>
              Items become selectable once they’re received back from the karigar.
            </div>
          )}
        </div>
        )}
      </div>
    </div>
    

    {/* 🔥 MODALS (UNCHANGED) */}
    {showReceipt && (
      <ReceiptModal
        order={order}
        onClose={() => setShowReceipt(false)}
      />
    )}

    {showAddStitch && (
      <AddStitchModal
        orderId={orderId}
        defaultCustomerId={order.customerId}
        onClose={() => {
          setShowAddStitch(false);
          load(); // 🔥 refresh order data
        }}
      />
    )}
    {showAddMaterial && (
      <AddMaterialModal
        orderId={orderId}
        defaultCustomerId={order.customerId}
        onClose={() => {
          setShowAddMaterial(false);
          load(); // 🔥 refresh order data
        }}
      />
    )}

    {deleteMaterialId && (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <h3 style={{ marginBottom: 10 }}>Delete Material</h3>

          <p style={{ marginBottom: 20 }}>
            Are you sure you want to delete this material?
          </p>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              className="btn btn-ghost"
              onClick={() => setDeleteMaterialId(null)}
            >
              Cancel
            </button>

            <button
              className="btn btn-danger"
              onClick={handleDeleteMaterial}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}
    {deleteStitchId && (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <h3 style={{ marginBottom: 10 }}>Delete Stitch</h3>

          <p style={{ marginBottom: 20 }}>
            Are you sure you want to delete this stitch?
          </p>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              className="btn btn-ghost"
              onClick={() => setDeleteStitchID(null)}
            >
              Cancel
            </button>

            <button
              className="btn btn-danger"
              onClick={handleDeleteStitch}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}

  </div>
);
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    width: 350,
    background: "#fff",
    borderRadius: 10,
    padding: 20,
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  },
};